<script setup lang="ts">
  import { computed, getCurrentInstance, nextTick, ref } from 'vue';
  import { useStableWindowSize } from '@/composition/use-stable-window-size';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiSegmented from '@/components/pos-v2/ui/PosUiSegmented.vue';
  import PosUiSwitch from '@/components/pos-v2/ui/PosUiSwitch.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import V2ReservationsDateNav from './V2ReservationsDateNav.vue';
  defineProps<{
    date: string;
    today: string;
    view: string;
    search: string;
    onlineOpen: boolean;
    onlineBusy: boolean;
    canPrint: boolean;
  }>();
  const emit = defineEmits<{
    (event: 'date', date: string): void;
    (event: 'view', view: string): void;
    (event: 'search', query: string): void;
    (event: 'online', open: boolean): void;
    (event: 'create'): void;
    (event: 'walkIn'): void;
    (event: 'print'): void;
  }>();
  const searchExpanded = ref(false);
  const searchRoot = ref<HTMLElement | null>(null);
  const { width } = useStableWindowSize();
  const compact = computed(() => width.value < 768);
  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string): string {
    return String(proxy.$t(key));
  }
  const views = computed(() =>
    ['list', 'timeline', 'floor'].map((id) => ({
      id,
      label: i18nT(`reservations.view.${id}`),
    }))
  );
  async function focusSearch() {
    searchExpanded.value = true;
    await nextTick();
    searchRoot.value?.querySelector('input')?.focus();
  }
  function toggleSearch() {
    if (searchExpanded.value) searchExpanded.value = false;
    else void focusSearch();
  }
  defineExpose({ focusSearch });
</script>

<template>
  <div class="reservations-command-bar">
    <div class="command-main">
      <V2ReservationsDateNav
        :value="date"
        :today="today"
        @input="emit('date', $event)"
      />
      <div
        ref="searchRoot"
        class="command-search"
        :class="{ expanded: searchExpanded }"
      >
        <PosUiInput
          :value="search"
          icon="magnify"
          clearable
          :placeholder="$t('reservations.bar.searchPlaceholder')"
          @input="emit('search', $event)"
        />
      </div>
      <PosUiButton
        class="search-toggle"
        color="neutral"
        variant="ghost"
        icon="magnify"
        :aria-label="$t('reservations.bar.searchPlaceholder')"
        :title="$t('reservations.bar.searchPlaceholder')"
        @click="toggleSearch"
      />
    </div>
    <div class="command-actions">
      <PosUiSegmented
        v-if="!compact"
        class="view-toggle"
        :value="view"
        :items="views"
        @input="emit('view', $event)"
      />
      <PosUiSwitch
        v-if="date === today"
        class="online-wide"
        :value="onlineOpen"
        :disabled="onlineBusy"
        :label="
          $t(
            onlineOpen
              ? 'reservations.bar.onlineOpen'
              : 'reservations.bar.onlineClosed'
          )
        "
        @input="emit('online', $event)"
      />
      <PosUiButton
        v-if="!compact"
        class="walk-in"
        color="neutral"
        variant="outline"
        icon="walk"
        :aria-label="$t('reservations.bar.walkIn')"
        :title="`${$t('reservations.bar.walkIn')} (W)`"
        @click="emit('walkIn')"
        ><span class="action-label">{{
          $t('reservations.bar.walkIn')
        }}</span></PosUiButton
      >
      <PosUiButton
        icon="plus"
        class="new-booking"
        :aria-label="$t('reservations.bar.new')"
        :title="`${$t('reservations.bar.new')} (N)`"
        @click="emit('create')"
        ><span class="action-label">{{ $t('reservations.bar.new') }}</span
        ><span class="action-label-short">{{
          $t('reservations.bar.newShort')
        }}</span></PosUiButton
      >
      <PosUiPopover v-if="compact || canPrint" placement="bottom" align="end">
        <template #trigger="{ toggle }"
          ><PosUiButton
            color="neutral"
            variant="ghost"
            icon="dots-horizontal"
            :aria-label="$t('posV2.moreActions')"
            :title="$t('posV2.moreActions')"
            @click="toggle"
        /></template>
        <template #default="{ close }">
          <div class="more-actions">
            <PosUiButton
              v-if="compact"
              color="neutral"
              variant="ghost"
              icon="walk"
              :title="$t('reservations.bar.walkIn')"
              @click="
                close();
                emit('walkIn');
              "
              >{{ $t('reservations.bar.walkIn') }}</PosUiButton
            >
            <PosUiSegmented
              v-if="compact"
              :value="view"
              :items="views"
              @input="
                emit('view', $event);
                close();
              "
            />
            <PosUiSwitch
              v-if="compact && date === today"
              class="online-menu"
              :value="onlineOpen"
              :disabled="onlineBusy"
              :label="
                $t(
                  onlineOpen
                    ? 'reservations.bar.onlineOpen'
                    : 'reservations.bar.onlineClosed'
                )
              "
              @input="emit('online', $event)"
            />
            <PosUiButton
              v-if="canPrint"
              color="neutral"
              variant="ghost"
              icon="printer-outline"
              @click="
                emit('print');
                close();
              "
              >{{ $t('reservations.bar.print') }}</PosUiButton
            >
          </div>
        </template>
      </PosUiPopover>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .reservations-command-bar {
    @apply tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-3 tw-py-3;
    .command-main {
      @apply tw-relative tw-flex tw-flex-wrap tw-items-center;
    }
    .command-search {
      @apply tw-hidden;
      &.expanded {
        @apply tw-order-3 tw-mt-2 tw-block tw-w-full;
      }
    }
    .search-toggle {
      @apply tw-ml-auto;
    }
    .command-actions {
      @apply tw-mt-3 tw-flex tw-items-center tw-justify-end;
      > * + * {
        @apply tw-ml-2;
      }
    }
    .view-toggle {
      @apply tw-mr-auto;
    }
    .online-wide {
      @apply tw-hidden;
    }
    .action-label {
      @apply tw-hidden;
    }
    @screen md {
      .action-label {
        @apply tw-inline;
      }
      .action-label-short {
        @apply tw-hidden;
      }
      // Tablets get the inline switch; the "⋯" menu keeps only printing.
      .online-wide {
        @apply tw-flex tw-max-w-[9rem] tw-text-xs;
      }
      .command-search {
        @apply tw-ml-4 tw-block tw-min-w-0 tw-flex-1;
        &.expanded {
          @apply tw-order-none tw-mt-0 tw-w-auto;
        }
      }
      .search-toggle {
        @apply tw-hidden;
      }
    }
    @media (min-width: 1280px) {
      @apply tw-flex tw-items-center tw-justify-between;
      .command-main {
        @apply tw-mr-3 tw-min-w-0 tw-flex-1;
      }
      .command-search {
        @apply tw-ml-2 tw-min-w-[9rem];
      }
      .command-actions {
        @apply tw-mt-0;
      }
    }
    @media (min-width: 1600px) {
      .online-wide {
        @apply tw-max-w-[13rem];
      }
    }
  }
  .more-actions {
    @apply tw-min-w-[15rem] tw-p-3;
    > * + * {
      @apply tw-mt-2;
    }
    .online-menu {
      @apply tw-w-full;
    }
  }
</style>
